import { FC, useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    Tooltip
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ContentCopy as CopyIcon,
    ExpandMore as ExpandMoreIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    VpnKey as KeyIcon
} from "@mui/icons-material";
import { adminApiClient } from "../utils/adminApiClient";
import { useSnackbar } from "notistack";
import { IClient } from "@tintoa/identity-service-client";

interface ApiKeyFormData {
    name: string;
}

export const ApiKeyManagement: FC = () => {
    const [clients, setClients] = useState<IClient[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
    const [formData, setFormData] = useState<ApiKeyFormData>({ name: "" });
    const [errors, setErrors] = useState<Partial<ApiKeyFormData>>({});
    const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            setLoading(true);
            const data = await adminApiClient.getClients();
            setClients(data);
        } catch (error: any) {
            console.error("Failed to load clients:", error);
            enqueueSnackbar(
                `Fehler beim Laden der Clients: ${
                    error.message || "Unbekannter Fehler"
                }`,
                { variant: "error" }
            );
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (client: IClient) => {
        setSelectedClient(client);
        setFormData({ name: "" });
        setErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedClient(null);
        setFormData({ name: "" });
        setErrors({});
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<ApiKeyFormData> = {};

        if (!formData.name.trim()) {
            newErrors.name = "Name ist erforderlich";
        }

        // Check if name already exists for this client
        if (
            selectedClient &&
            selectedClient.apiKeys.some(
                (key: { name: string }) => key.name === formData.name.trim()
            )
        ) {
            newErrors.name = "Ein API-Key mit diesem Namen existiert bereits";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm() || !selectedClient) return;

        try {
            const result = await adminApiClient.addApiKey(
                selectedClient.id,
                formData
            );
            enqueueSnackbar("API-Key erfolgreich erstellt", {
                variant: "success"
            });

            // Show the generated key to the user
            if (result && result.value) {
                enqueueSnackbar(`API-Key erstellt: ${result.value}`, {
                    variant: "info",
                    autoHideDuration: 10000,
                    action: (
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() =>
                                navigator.clipboard.writeText(
                                    result.value || ""
                                )
                            }
                        >
                            Kopieren
                        </Button>
                    )
                });
            }

            handleCloseDialog();
            loadClients();
        } catch (error: any) {
            console.error("Failed to create API key:", error);
            enqueueSnackbar(
                `Fehler beim Erstellen des API-Keys: ${
                    error.message || "Unbekannter Fehler"
                }`,
                { variant: "error" }
            );
        }
    };

    const handleDeleteApiKey = async (client: IClient, apiKeyName: string) => {
        if (
            window.confirm(
                `Möchten Sie den API-Key "${apiKeyName}" für Client "${client.name}" wirklich löschen?`
            )
        ) {
            try {
                await adminApiClient.removeApiKey(client.id, apiKeyName);
                enqueueSnackbar("API-Key erfolgreich gelöscht", {
                    variant: "success"
                });
                loadClients();
            } catch (error: any) {
                console.error("Failed to delete API key:", error);
                enqueueSnackbar(
                    `Fehler beim Löschen des API-Keys: ${
                        error.message || "Unbekannter Fehler"
                    }`,
                    { variant: "error" }
                );
            }
        }
    };

    const toggleKeyVisibility = (clientId: string, keyName: string) => {
        const key = `${clientId}-${keyName}`;
        const newVisibleKeys = new Set(visibleKeys);
        if (newVisibleKeys.has(key)) {
            newVisibleKeys.delete(key);
        } else {
            newVisibleKeys.add(key);
        }
        setVisibleKeys(newVisibleKeys);
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar("In Zwischenablage kopiert", {
                variant: "success"
            });
        } catch (error) {
            enqueueSnackbar("Fehler beim Kopieren", { variant: "error" });
        }
    };

    const maskApiKey = (apiKey: string) => {
        if (apiKey.length <= 12) return "••••••••••••";
        return (
            apiKey.substring(0, 8) +
            "••••••••••••" +
            apiKey.substring(apiKey.length - 4)
        );
    };

    if (loading) {
        return (
            <Box sx={{ width: "100%" }}>
                <LinearProgress />
            </Box>
        );
    }

    const totalApiKeys = clients.reduce(
        (total, client) => total + client.apiKeys.length,
        0
    );

    return (
        <Box>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3
                }}
            >
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        API-Key Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Verwalten Sie API-Keys für Client-Anwendungen
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Chip
                        icon={<KeyIcon />}
                        label={`${totalApiKeys} API-Keys gesamt`}
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </Box>


            {/* Clients with API Keys */}
            {clients.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                        Keine Clients gefunden. Erstellen Sie zuerst einen
                        Client.
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {clients.map((client) => (
                        <Accordion
                            key={client.id}
                            defaultExpanded={client.apiKeys.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        width: "100%"
                                    }}
                                >
                                    <Typography variant="h6" component="div">
                                        {client.name}
                                    </Typography>
                                    <Chip
                                        label={`${client.apiKeys.length} API-Keys`}
                                        color={
                                            client.apiKeys.length > 0
                                                ? "primary"
                                                : "default"
                                        }
                                        size="small"
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ ml: "auto" }}
                                    >
                                        API ID: {client.apiId}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        {client.description}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpenDialog(client)}
                                        size="small"
                                    >
                                        API-Key hinzufügen
                                    </Button>
                                </Box>

                                {client.apiKeys.length === 0 ? (
                                    <Box
                                        sx={{
                                            p: 3,
                                            textAlign: "center",
                                            bgcolor: "grey.50",
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Keine API-Keys für diesen Client
                                            vorhanden
                                        </Typography>
                                    </Box>
                                ) : (
                                    <TableContainer
                                        component={Paper}
                                        variant="subtle"
                                    >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>
                                                        API-Key
                                                    </TableCell>
                                                    <TableCell>
                                                        Aktionen
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {client.apiKeys.map(
                                                    (
                                                        apiKey: {
                                                            name: string;
                                                            value?: string | null;
                                                        },
                                                        index: number
                                                    ) => {
                                                        const keyId = `${client.id}-${apiKey.name}`;
                                                        const isVisible =
                                                            visibleKeys.has(
                                                                keyId
                                                            );
                                                        return (
                                                            <TableRow
                                                                key={index}
                                                                hover
                                                            >
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        fontWeight="medium"
                                                                    >
                                                                        {
                                                                            apiKey.name
                                                                        }
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            alignItems:
                                                                                "center",
                                                                            gap: 1
                                                                        }}
                                                                    >
                                                                        <TextField
                                                                            value={
                                                                                isVisible
                                                                                    ? apiKey.value ||
                                                                                      ""
                                                                                    : maskApiKey(
                                                                                          apiKey.value ||
                                                                                              ""
                                                                                      )
                                                                            }
                                                                            variant="outlined"
                                                                            size="small"
                                                                            fullWidth
                                                                            InputProps={{
                                                                                readOnly:
                                                                                    true,
                                                                                endAdornment:
                                                                                    (
                                                                                        <InputAdornment position="end">
                                                                                            <Tooltip
                                                                                                title={
                                                                                                    isVisible
                                                                                                        ? "Verstecken"
                                                                                                        : "Anzeigen"
                                                                                                }
                                                                                            >
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    onClick={() =>
                                                                                                        toggleKeyVisibility(
                                                                                                            client.id,
                                                                                                            apiKey.name
                                                                                                        )
                                                                                                    }
                                                                                                >
                                                                                                    {isVisible ? (
                                                                                                        <VisibilityOffIcon />
                                                                                                    ) : (
                                                                                                        <VisibilityIcon />
                                                                                                    )}
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                            <Tooltip title="Kopieren">
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    onClick={() =>
                                                                                                        copyToClipboard(
                                                                                                            apiKey.value ||
                                                                                                                ""
                                                                                                        )
                                                                                                    }
                                                                                                >
                                                                                                    <CopyIcon />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                        </InputAdornment>
                                                                                    ),
                                                                                sx: {
                                                                                    fontFamily:
                                                                                        "monospace"
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            handleDeleteApiKey(
                                                                                client,
                                                                                apiKey.name
                                                                            )
                                                                        }
                                                                        color="error"
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    }
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            {/* Create API Key Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Neuen API-Key erstellen
                    {selectedClient && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            für Client: {selectedClient.name}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="API-Key Name"
                            value={formData.name}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    name: e.target.value
                                })
                            }
                            error={!!errors.name}
                            helperText={
                                errors.name ||
                                "Eindeutiger Name für diesen API-Key"
                            }
                            placeholder="z.B. production-key, staging-key"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Abbrechen</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        API-Key erstellen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
